import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import Usermanualpage from "./pages/LandingPage/Others/Usermanualpage";
import { HomeExpenseSkeleton, ReportsSkeleton, SearchListSkeleton, ProfilePageSkeleton } from "./components/ui/Skeletons";

// Lazy-loaded Landing Pages
const Termsofservice = lazy(() => import("./pages/LandingPage/Others/Termsofservice"));
const Privacypolicy = lazy(() => import("./pages/LandingPage/Others/Privacypolicy"));
const Sharepage = lazy(() => import("./pages/LandingPage/Others/Share"));
const Addreview = lazy(() => import("./pages/LandingPage/Others/AddReview"));
const ContactUs = lazy(() => import("./pages/LandingPage/Others/ContactUs"));
const MeetOutflow = lazy(() => import("./pages/LandingPage/Others/MeetOutflow"));
const Blog = lazy(() => import("./pages/LandingPage/Others/Blog"));

// Layout (Lazy-loaded)
const MainLayout = lazy(() => import("./components/layout/MainLayout"));

// Public Pages (Lazy-loaded)
const AuthPage = lazy(() => import("./pages/Login/AuthPage"));
const LandingPage = lazy(() => import("./pages/LandingPage/LandingPage"));
const Install = lazy(() => import("./pages/LandingPage/Others/Install"));
const NotFound = lazy(() => import("./pages/NotFound/NotFound"));

// Protected Pages (Lazy-loaded)
const Home = lazy(() => import("./pages/Home/Homemain"));
const Search = lazy(() => import("./pages/Search/SearchMain"));
const Reports = lazy(() => import("./pages/Reports/ReportsScreen"));
const ExpenseCategory = lazy(() => import("./pages/Add Expense/ExpenseCategory"));
const ProfileMain = lazy(() => import("./pages/Profile/ProfileMain"));
const EditProfile = lazy(() => import("./pages/Profile/EditProfile"));
const SetBudget = lazy(() => import("./pages/Profile/SetBudget"));
const Currency = lazy(() => import("./pages/Profile/Currency"));
const Notifications = lazy(() => import("./pages/Profile/Notifications"));
const ExportData = lazy(() => import("./pages/Profile/ExportData"));
const ReportBug = lazy(() => import("./pages/LandingPage/Others/ReportBug"));
const ProfileStats = lazy(() => import("./pages/Profile/ProfileStats"));

function AppRoutes({ user, isAuthLoading }) {
  // App.js handles the loading/splash state now

  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Public Routes */}
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/usermanual" element={<Usermanualpage />} />

        <Route path="/privacy" element={<Privacypolicy />} />
        <Route path="/terms" element={<Termsofservice />} />
        <Route path="/share" element={<Sharepage />} />
        <Route path="/add-review" element={<Addreview />} />
        <Route path="/reportbug" element={<ReportBug />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/meet-outflow" element={<MeetOutflow />} />

        {/* Auth Routes - Redirect to / if logged in */}
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthPage mode="login" />} />
        <Route path="/signup" element={user ? <Navigate to="/" replace /> : <AuthPage mode="signup" />} />
        <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <AuthPage mode="forgot" />} />
        <Route path="/install" element={<Install />} />

        {/* Root Route: Conditional Rendering based on Auth */}
        <Route 
          path="/" 
          element={
            user ? (
              <MainLayout />
            ) : isAuthLoading ? (
              <div className="bg-[#000000] min-h-screen" />
            ) : (
              <LandingPage />
            )
          }
        >
          {/* Protected Nested Routes (only accessible if user is present) */}
          {(user || isAuthLoading) && (
            <>
              <Route index element={<Suspense fallback={<HomeExpenseSkeleton />}><Home /></Suspense>} />
              <Route path="reports" element={<Suspense fallback={<ReportsSkeleton />}><Reports /></Suspense>} />
              <Route path="expensecategory" element={<ExpenseCategory />} />
              <Route path="search" element={<Suspense fallback={<SearchListSkeleton />}><Search /></Suspense>} />
              <Route path="profile" element={<Suspense fallback={<ProfilePageSkeleton />}><ProfileMain /></Suspense>} />
              <Route path="edit-profile" element={<EditProfile />} />
              <Route path="setbudget" element={<SetBudget />} />
              <Route path="currency" element={<Currency />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="exportdata" element={<ExportData />} />
              <Route path="profilestats" element={<ProfileStats />} />
            </>
          )}
        </Route>

        {/* Catch-all Route */}
        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
